import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { PayPalButton } from './base';

import { NewSubscription } from '@services/subscription/types/new-subscription';
import * as subscriptions from '@webapp/sdk/subscriptions';
import { CreateSubscriptionSuccess } from '@webapp/store/subscription/action-creators';
import { Subscription } from '@services/subscription/types/subscription';
import AppState from '../../../store/state';
import {
  getSubscriptionChange,
  isNewSubscription,
} from '../../../store/subscription/selectors';
import { PlanNames } from '@services/product/types/plan-names';
import { SubscriptionType } from '@services/subscription/types/subscription-type';
import { Order } from '@webapp/types/orders/order';
import { CustomerMetadata } from '@services/payment/types/api/customer-metadata';

interface PayPalSubscriptionButton {
  className?: string;
  paypalPlanId?: string;
  newSubscription: NewSubscription;
  subscriptionType: SubscriptionType;
  currentSubscription?: Subscription;
  getClientId: () => Promise<string>;
  onApprove: (data: any) => Promise<void>;
}

interface StateProps {
  isNew: boolean;
  change?: 'FREQUENCY' | 'IGNORE' | 'DOWNGRADE' | 'UPGRADE' | undefined;
}

interface DispatchProps {
  createSubscriptionSuccess: (
    subscription: Subscription,
    isNew: boolean,
    subscriptionType: SubscriptionType,
    change: 'FREQUENCY' | 'DOWNGRADE' | 'UPGRADE' | undefined,
    order: Order,
    customer: CustomerMetadata,
    currentSubscription?: Subscription
  ) => CreateSubscriptionSuccess;
}

const mapStateToProps = (
  _: AppState,
  props: PayPalSubscriptionButton
): StateProps => {
  return {
    change: getSubscriptionChange(
      props.currentSubscription,
      props.newSubscription.plan.toLowerCase() as PlanNames,
      props.newSubscription.frequency
    ),
    isNew: isNewSubscription(props.currentSubscription),
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
  createSubscriptionSuccess: (
    subscription: Subscription,
    isNew: boolean,
    subscriptionType: SubscriptionType,
    change: 'FREQUENCY' | 'DOWNGRADE' | 'UPGRADE' | undefined,
    order: Order,
    customer: CustomerMetadata,
    currentSubscription?: Subscription
  ) =>
    dispatch(
      new CreateSubscriptionSuccess(
        subscription,
        isNew,
        subscriptionType,
        order,
        customer,
        change,
        currentSubscription
      )
    ),
});

export const PayPalSubscriptionButton: React.FC<
  PayPalSubscriptionButton & StateProps & DispatchProps
> = ({
  className,
  getClientId,
  paypalPlanId,
  isNew,
  currentSubscription,
  onApprove,
}) => {
  const createSubscription = async (_: any, actions: any) => {
    const clientId = await getClientId();

    if (!clientId) {
      throw new Error('onApprove NO CLIENT ID');
    }

    if (!isNew) {
      if (currentSubscription?.paypalId) {
        return actions.subscription.revise(currentSubscription?.paypalId, {
          plan_id: paypalPlanId,
        });
      }

      if (currentSubscription?.externalId) {
        await subscriptions.cancel(currentSubscription.clientId);
      }
    }

    return actions.subscription.create({
      plan_id: paypalPlanId,
      custom_id: clientId,
    });
  };

  if (!paypalPlanId) {
    return null;
  }

  return (
    <div className={className}>
      <PayPalButton
        style={{
          layout: 'vertical',
          color: 'blue',
          tagline: false,
          size: 'responsive',
          height: 55,
        }}
        createSubscription={createSubscription}
        onApprove={onApprove}
      />
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PayPalSubscriptionButton);
